import { useContext, useEffect, useState } from 'react';
import { Alert, Button, Card, Input, InputNumber, Modal, Select, Space, Spin, Table, Tag, Upload, message } from 'antd';
import { CheckOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import axios from '../config/axios';
import dayjs from '../config/dayjs';
import { AuthContext } from '../context/auth.context';
import { Device } from '../types/device.types';
import { formatNumber, handleAPIError } from '../utils/helpers';

interface ScheduleRow {
	due_date: string;
	amount: string;
	number?: number;
	paid?: string;
}

interface Proposal {
	kind: 'plan_change' | 'repair';
	from_plan: string;
	to_plan: string | null;
	amount: string;
	repair_cost: string | null;
	repair_interest: string | null;
	repair_months: number | null;
	before: ScheduleRow[];
	after: ScheduleRow[];
}

interface Amendment {
	id: number;
	kind: 'plan_change' | 'repair';
	reason: string;
	from_plan: string | null;
	to_plan: string | null;
	amount: string;
	repair_cost: string | null;
	repair_interest: string | null;
	repair_months: number | null;
	repairer: string | null;
	receipt: string | null;
	schedule_before: ScheduleRow[];
	schedule_after: ScheduleRow[];
	created_by: string | null;
	created_at: string;
}

const PLANS: Device['payment_plan'][] = ['daily', 'weekly', 'monthly'];

const ugx = (v: string | number) => `UGX ${formatNumber(v)}`;
const day = (v: string) => dayjs(v).format('DD MMM YYYY');

const errorText = (error: unknown) =>
	error instanceof AxiosError ? error.response?.data?.msg ?? error.message : String(error);

const ScheduleTable = ({ rows }: { rows: ScheduleRow[] }) => (
	<Table
		size="small"
		rowKey="due_date"
		pagination={false}
		dataSource={rows}
		scroll={{ y: 240 }}
		columns={[
			{ title: 'Due', dataIndex: 'due_date', render: day },
			{ title: 'Amount', dataIndex: 'amount', align: 'right' as const, render: ugx },
		]}
	/>
);

const useDebounced = <T,>(value: T, ms = 400) => {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const t = setTimeout(() => setDebounced(value), ms);
		return () => clearTimeout(t);
	}, [value, ms]);
	return debounced;
};

const refresh = (queryClient: ReturnType<typeof useQueryClient>, device: Device) => {
	queryClient.invalidateQueries({ queryKey: ['device', device.id] });
	queryClient.invalidateQueries({ queryKey: ['devices'] });
	queryClient.invalidateQueries({ queryKey: ['device-installments', device.id] });
	queryClient.invalidateQueries({ queryKey: ['device-amendments', device.id] });
};

const ChangePlanModal = ({ device, open, onClose }: { device: Device; open: boolean; onClose: () => void }) => {
	const queryClient = useQueryClient();
	const [plan, setPlan] = useState<string | undefined>();
	const [reason, setReason] = useState('');

	const close = () => {
		setPlan(undefined);
		setReason('');
		onClose();
	};

	const preview = useQuery<Proposal>({
		queryKey: ['change-plan-preview', device.id, plan],
		enabled: open && !!plan,
		retry: false,
		gcTime: 0,
		queryFn: async () => {
			const res = await axios.post(
				`/admin/super/devices/${device.id}/change-plan`,
				{ payment_plan: plan, preview: true },
				{ withCredentials: true },
			);
			return res.data.results;
		},
	});

	const apply = useMutation({
		mutationFn: async () => {
			await axios.post(
				`/admin/super/devices/${device.id}/change-plan`,
				{ payment_plan: plan, reason: reason.trim() },
				{ withCredentials: true },
			);
		},
		onSuccess: () => {
			message.success(`Moved to the ${plan} plan`);
			refresh(queryClient, device);
			close();
		},
		onError: handleAPIError,
	});

	const options = PLANS.filter(
		(p) => p !== device.payment_plan && !(p === 'monthly' && device.duration_unit === 'week'),
	).map((p) => ({ value: p, label: p.charAt(0).toUpperCase() + p.slice(1) }));

	return (
		<Modal
			open={open}
			title={`Change plan · currently ${device.payment_plan}`}
			okText="Change plan"
			okButtonProps={{ disabled: !preview.data || !reason.trim() || preview.isError }}
			confirmLoading={apply.isPending}
			onCancel={close}
			onOk={() => apply.mutate()}
			width={520}
			destroyOnClose
		>
			<Space
				direction="vertical"
				size={12}
				style={{ width: '100%' }}
			>
				<Select
					style={{ width: '100%' }}
					placeholder="New plan"
					value={plan}
					onChange={setPlan}
					options={options}
				/>
				{preview.isFetching && <Spin size="small" />}
				{preview.isError && (
					<Alert
						type="error"
						showIcon
						message={errorText(preview.error)}
					/>
				)}
				{preview.data && !preview.isFetching && (
					<>
						<div>
							{ugx(preview.data.amount)} over {preview.data.after.length}{' '}
							{preview.data.after.length === 1 ? 'installment' : 'installments'}, replacing{' '}
							{preview.data.before.length}
						</div>
						<ScheduleTable rows={preview.data.after} />
					</>
				)}
				<Input.TextArea
					rows={2}
					placeholder="Reason"
					maxLength={255}
					value={reason}
					onChange={(e) => setReason(e.target.value)}
				/>
			</Space>
		</Modal>
	);
};

const RepairModal = ({ device, open, onClose }: { device: Device; open: boolean; onClose: () => void }) => {
	const queryClient = useQueryClient();
	const [cost, setCost] = useState<number | null>(null);
	const [repairer, setRepairer] = useState('');
	const [reason, setReason] = useState('');
	const [receipt, setReceipt] = useState<string | null>(null);
	const [uploading, setUploading] = useState(false);
	const debouncedCost = useDebounced(cost);

	const close = () => {
		setCost(null);
		setRepairer('');
		setReason('');
		setReceipt(null);
		onClose();
	};

	const preview = useQuery<Proposal>({
		queryKey: ['repair-preview', device.id, debouncedCost],
		enabled: open && !!debouncedCost && debouncedCost > 0,
		retry: false,
		gcTime: 0,
		queryFn: async () => {
			const res = await axios.post(
				`/admin/super/devices/${device.id}/repair`,
				{ repair_cost: debouncedCost, preview: true },
				{ withCredentials: true },
			);
			return res.data.results;
		},
	});

	const apply = useMutation({
		mutationFn: async () => {
			await axios.post(
				`/admin/super/devices/${device.id}/repair`,
				{ repair_cost: cost, repairer: repairer.trim(), reason: reason.trim(), receipt },
				{ withCredentials: true },
			);
		},
		onSuccess: () => {
			message.success('Repair added to the contract');
			refresh(queryClient, device);
			close();
		},
		onError: handleAPIError,
	});

	const upload = async (file: File) => {
		setUploading(true);
		const form = new FormData();
		form.append('file', file);
		try {
			const res = await axios.post('/fundamentals/file-upload', form, { withCredentials: true });
			setReceipt(res.data?.results?.secure_url ?? null);
		} catch (error) {
			handleAPIError(error as Error);
		} finally {
			setUploading(false);
		}
		return false;
	};

	const ready =
		!!preview.data && !preview.isError && cost === debouncedCost && !!repairer.trim() && !!reason.trim() && !uploading;

	return (
		<Modal
			open={open}
			title="Add repair"
			okText="Add repair"
			okButtonProps={{ disabled: !ready }}
			confirmLoading={apply.isPending}
			onCancel={close}
			onOk={() => apply.mutate()}
			width={520}
			destroyOnClose
		>
			<Space
				direction="vertical"
				size={12}
				style={{ width: '100%' }}
			>
				<InputNumber
					style={{ width: '100%' }}
					placeholder="Repair cost (UGX)"
					min={1}
					precision={0}
					value={cost}
					onChange={(v) => setCost(v)}
					formatter={(v) => (v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
					parser={(v) => Number((v ?? '').replace(/,/g, ''))}
				/>
				<Input
					placeholder="Repaired by"
					maxLength={100}
					value={repairer}
					onChange={(e) => setRepairer(e.target.value)}
				/>
				<Upload
					accept="image/*,application/pdf"
					maxCount={1}
					showUploadList={false}
					beforeUpload={upload}
				>
					<Button
						icon={receipt ? <CheckOutlined /> : <UploadOutlined />}
						loading={uploading}
					>
						{receipt ? 'Receipt attached' : 'Receipt'}
					</Button>
				</Upload>
				{preview.isFetching && <Spin size="small" />}
				{preview.isError && (
					<Alert
						type="error"
						showIcon
						message={errorText(preview.error)}
					/>
				)}
				{preview.data && !preview.isFetching && (
					<>
						<div>
							{ugx(preview.data.repair_cost!)} + {ugx(preview.data.repair_interest!)} interest (
							{preview.data.repair_months} {preview.data.repair_months === 1 ? 'month' : 'months'}) ={' '}
							<strong>{ugx(preview.data.amount)}</strong> over {preview.data.after.length}{' '}
							{preview.data.after.length === 1 ? 'installment' : 'installments'}
						</div>
						<ScheduleTable rows={preview.data.after} />
					</>
				)}
				<Input.TextArea
					rows={2}
					placeholder="Reason"
					maxLength={255}
					value={reason}
					onChange={(e) => setReason(e.target.value)}
				/>
			</Space>
		</Modal>
	);
};

export const ContractAmendmentActions = ({ device }: { device: Device }) => {
	const { user } = useContext(AuthContext);
	const isSuperAdmin = user?.is_superuser || user?.admin_type === 'super_admin';
	const [planOpen, setPlanOpen] = useState(false);
	const [repairOpen, setRepairOpen] = useState(false);

	const live =
		device.status === 'approved' && !device.is_completed && !device.written_off_at && !device.is_recovered;
	if (!isSuperAdmin || !live) return null;

	return (
		<>
			<Button
				size="small"
				onClick={() => setPlanOpen(true)}
			>
				Change plan
			</Button>
			<Button
				size="small"
				onClick={() => setRepairOpen(true)}
			>
				Add repair
			</Button>
			<ChangePlanModal
				device={device}
				open={planOpen}
				onClose={() => setPlanOpen(false)}
			/>
			<RepairModal
				device={device}
				open={repairOpen}
				onClose={() => setRepairOpen(false)}
			/>
		</>
	);
};

export const ContractAmendmentHistory = ({ device }: { device: Device }) => {
	const { data = [] } = useQuery<Amendment[]>({
		queryKey: ['device-amendments', device.id],
		queryFn: async () => {
			const res = await axios.get(`/admin/devices/${device.id}/amendments`, { withCredentials: true });
			return res.data.results;
		},
	});

	if (!data.length) return null;

	return (
		<Card
			size="small"
			title="Contract changes"
		>
			<Table
				size="small"
				rowKey="id"
				pagination={false}
				dataSource={data}
				expandable={{
					expandedRowRender: (a: Amendment) => (
						<div style={{ display: 'flex', gap: 16 }}>
							<div style={{ flex: 1 }}>
								<strong>Before</strong>
								<ScheduleTable rows={a.schedule_before} />
							</div>
							<div style={{ flex: 1 }}>
								<strong>After</strong>
								<ScheduleTable rows={a.schedule_after} />
							</div>
						</div>
					),
				}}
				columns={[
					{ title: 'Date', dataIndex: 'created_at', render: (v: string) => dayjs(v).format('DD MMM YYYY, HH:mm') },
					{
						title: 'Change',
						render: (a: Amendment) =>
							a.kind === 'plan_change' ? (
								<Space size={4}>
									<Tag color="blue">Plan</Tag>
									{a.from_plan} → {a.to_plan}
								</Space>
							) : (
								<Space size={4}>
									<Tag color="purple">Repair</Tag>
									{ugx(a.repair_cost!)} + {ugx(a.repair_interest!)} interest · {a.repairer}
									{a.receipt && (
										<a
											href={a.receipt}
											target="_blank"
											rel="noreferrer"
										>
											receipt
										</a>
									)}
								</Space>
							),
					},
					{ title: 'Amount', dataIndex: 'amount', align: 'right' as const, render: ugx },
					{ title: 'By', dataIndex: 'created_by' },
					{ title: 'Reason', dataIndex: 'reason' },
				]}
			/>
		</Card>
	);
};
